import Source from '../source';
import Playlist from './playlist';
import SyncDirection from './syncDirection';
import SyncSource from './syncSource';
import SyncType from './syncType';

/** Sources stored before directions existed have none; they behave as two-way. */
export function sourceDirection(source: SyncSource) {
  return source.direction || SyncDirection.Both;
}

/** Whether changes made in Fadila are meant to reach the remote side. */
export function pushesToSource(source: SyncSource) {
  return sourceDirection(source) !== SyncDirection.FromSource;
}

/** Whether changes made at the remote side are meant to reach Fadila. */
export function pullsFromSource(source: SyncSource) {
  return sourceDirection(source) !== SyncDirection.ToSource;
}

/**
 * Which kind of track each sync source deals in.
 *
 * A provider only ever looks at its own kind and leaves the rest of the
 * playlist alone. That is what lets one Fadila playlist carry a YouTube
 * source and a SoundCloud source at once: the playlist is the union, each
 * remote holds its own half, and neither provider can see the other's
 * tracks - let alone try to push a video to SoundCloud.
 */
export const syncTypeTrackSource: { [P in SyncType]?: Source } = {
  [SyncType.YouTubePlaylist]: Source.YouTube,
  [SyncType.SoundCloudPlaylist]: Source.SoundCloud,
};

/**
 * The sync types that mirror a remote *playlist*, as opposed to the ones
 * that only ever pull on a schedule (podcasts). These sync when their
 * playlist is opened, when their provider's page is opened, shortly after a
 * local edit, and on request - never at startup.
 */
export const playlistSyncTypes: SyncType[] = [
  SyncType.YouTubePlaylist,
  SyncType.SoundCloudPlaylist,
];

export function isPlaylistSyncType(type: SyncType) {
  return playlistSyncTypes.indexOf(type) !== -1;
}

export function sourcesOfType(pl: Playlist, type: SyncType) {
  return pl.syncSources.filter((x) => x.type === type);
}

/** Every source on this playlist that mirrors a remote playlist. */
export function playlistSources(pl: Playlist) {
  return pl.syncSources.filter((x) => isPlaylistSyncType(x.type));
}

export function isSyncedWithPlaylist(pl: Playlist) {
  return playlistSources(pl).length > 0;
}

export function findSource(pl: Playlist, type: SyncType, sourceId: string) {
  return pl.syncSources.find((x) => x.type === type && x.sourceId === sourceId);
}

/**
 * The ids of the playlist's tracks that belong to this source's service, in
 * playlist order, duplicates kept.
 */
export function localIdsForSource(pl: Playlist, source: SyncSource) {
  const trackSource = syncTypeTrackSource[source.type];

  if (trackSource === undefined) {
    return [];
  }

  return pl.tracks.filter((x) => x.trackType === trackSource).map((x) => x.id);
}

export function youTubeSources(pl: Playlist) {
  return sourcesOfType(pl, SyncType.YouTubePlaylist);
}

export function isSyncedWithYouTube(pl: Playlist) {
  return youTubeSources(pl).length > 0;
}

export function findYouTubeSource(pl: Playlist, youTubePlaylistId: string) {
  return findSource(pl, SyncType.YouTubePlaylist, youTubePlaylistId);
}

/** A stable key for one source on one playlist, for in-flight bookkeeping. */
export function syncSourceKey(pl: Playlist, source: SyncSource) {
  return `${pl.id}|${source.type}|${source.sourceId}`;
}

/** The ids of a playlist's YouTube tracks, in playlist order, duplicates kept. */
export function localYouTubeIds(pl: Playlist) {
  return pl.tracks.filter((x) => x.trackType === Source.YouTube).map((x) => x.id);
}

/**
 * A new playlist object with the given remote playlist linked as a sync
 * source. Relinking an already-linked playlist replaces the source, keeping
 * whatever snapshot it had so the next sync does not treat everything as new.
 *
 * `lastUpdated: -1` is the existing convention for "sync me now" - the sync
 * service picks that up as soon as the playlist is saved.
 */
export function linkPlaylistSource(
  pl: Playlist,
  type: SyncType,
  sourceId: string,
  name: string,
  direction = SyncDirection.Both,
): Playlist {
  const existing = findSource(pl, type, sourceId);

  const source: SyncSource = {
    type,
    sourceId,
    lastUpdated: -1,
    direction,
    name,
    syncedIds: existing?.syncedIds,
  };

  return {
    ...pl,
    syncSources: pl.syncSources.filter((x) => x !== existing).concat(source),
  };
}

export function linkYouTubePlaylist(
  pl: Playlist,
  youTubePlaylistId: string,
  name: string,
  direction = SyncDirection.Both,
): Playlist {
  return linkPlaylistSource(pl, SyncType.YouTubePlaylist, youTubePlaylistId, name, direction);
}

export function unlinkSource(pl: Playlist, source: SyncSource): Playlist {
  return {
    ...pl,
    syncSources: pl.syncSources.filter(
      (x) => !(x.type === source.type && x.sourceId === source.sourceId),
    ),
  };
}

export function setSourceDirection(
  pl: Playlist,
  source: SyncSource,
  direction: SyncDirection,
): Playlist {
  return {
    ...pl,
    syncSources: pl.syncSources.map((x) =>
      x.type === source.type && x.sourceId === source.sourceId ? { ...x, direction } : x,
    ),
  };
}

/**
 * Replaces one source on a playlist. Used by sync providers to store the new
 * snapshot; matched by type and id because the provider may be holding an
 * older copy of the source object than the one now in the playlist.
 */
export function replaceSource(pl: Playlist, source: SyncSource): Playlist {
  return {
    ...pl,
    syncSources: pl.syncSources.map((x) =>
      x.type === source.type && x.sourceId === source.sourceId ? source : x,
    ),
  };
}

/**
 * Drops the ids this source has given up on.
 *
 * Applied to all three sides of the comparison - remote, local and the
 * snapshot - so a skipped id is not merely "not pushed" but absent from the
 * diff altogether. Filtering only one side would turn a skipped id into an
 * apparent removal and delete the track from the other end.
 */
export function visibleIds(source: SyncSource, ids: string[]) {
  if (!source.skippedIds || !source.skippedIds.length) {
    return ids;
  }

  const skipped = new Set(source.skippedIds);

  return ids.filter((id) => !skipped.has(id));
}

/** How many ids this playlist's sync sources have given up on. */
export function skippedCount(pl: Playlist) {
  return playlistSources(pl).reduce(
    (total, source) => total + (source.skippedIds ? source.skippedIds.length : 0),
    0,
  );
}

/**
 * Marks every source for an immediate sync. `retrySkipped` also clears the
 * given-up-on ids, so a video that failed for a reason since fixed - the
 * playlist was full, the video was temporarily private - gets another go.
 */
export function requestSync(pl: Playlist, retrySkipped = false): Playlist {
  return {
    ...pl,
    syncSources: pl.syncSources.map((source) => ({
      ...source,
      lastUpdated: -1,
      skippedIds: retrySkipped ? undefined : source.skippedIds,
    })),
  };
}

/**
 * A fingerprint of the playlist's content for one source's service, for
 * noticing local edits. Order-insensitive since ordering is not synced.
 */
export function localSignature(pl: Playlist, source: SyncSource) {
  return Array.from(new Set(localIdsForSource(pl, source)))
    .sort()
    .join(',');
}

export function localYouTubeSignature(pl: Playlist) {
  return localSignature(pl, { type: SyncType.YouTubePlaylist } as SyncSource);
}

/**
 * Whether the playlist's YouTube content differs from what the source last
 * saw. True after the user adds or removes a YouTube track locally - and also
 * after a remote removal, which cannot be told apart without asking YouTube;
 * a sync triggered by that is harmless and settles it.
 *
 * Always false before the first sync: linking already asks for one.
 */
export function hasPendingLocalChanges(pl: Playlist, source: SyncSource) {
  if (!source.syncedIds) {
    return false;
  }

  // Skipped ids are invisible to the sync, so a playlist holding nothing but
  // those is not pending anything. Leaving them in would report a change that
  // no sync can ever clear.
  const base = new Set(visibleIds(source, source.syncedIds));
  const local = new Set(visibleIds(source, localIdsForSource(pl, source)));

  if (base.size !== local.size) {
    return true;
  }

  for (const id of local) {
    if (!base.has(id)) {
      return true;
    }
  }

  return false;
}
